import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useToastMessage } from "../context/ToastMessageContext";
import {
  consumeSessionLogoutReason,
  SessionLogoutReason,
} from "../services/session-logout-reason";

const MESSAGES: Record<SessionLogoutReason, string> = {
  inactivity: "You have been logged out due to inactivity.",
  absolute: "Your session has expired. Please log in again.",
  org_access_changed: "Your organization access has changed. Please log in again.",
  unauthenticated: "Please log in to access this page.",
};

/**
 * Reads the notice reason persisted by `authService.logout(reason)` or
 * `ProtectedRoute` (stored in sessionStorage) and surfaces the matching
 * informational toast exactly once, on the public page the user lands on.
 *
 * Sources of the reason:
 * - Idle-timeout countdown (SessionTimeoutModal) → "inactivity"
 * - Explicit "Log Out Now" inside the idle warning modal → "inactivity"
 * - Org-picker inactivity timer (RoleSelection) → "inactivity"
 * - Server-side session invalidation via api-client 401/403 → "absolute"
 * - Org membership revoked during active session → "org_access_changed"
 * - Cold-load / in-session access of a protected URL with an invalid
 *   token via `ProtectedRoute` → "unauthenticated"
 *
 * Mounted inside `BrowserRouter` so it can observe client-side navigations
 * (e.g. `ProtectedRoute`'s `<Navigate />`) and display the notice for
 * same-tab redirects that don't remount the component, in addition to the
 * standard cold-load case.
 *
 * `consumeSessionLogoutReason()` clears the key after reading, so a manual
 * page refresh after the toast has been shown does not re-show it.
 */
export const SessionLogoutToast: React.FC = () => {
  const { showInfo } = useToastMessage();
  // `location.key` changes on every navigation (including `replace`), which
  // guarantees the effect re-runs after a `<Navigate />` inside ProtectedRoute.
  const location = useLocation();

  useEffect(() => {
    const reason = consumeSessionLogoutReason();
    if (!reason) return;
    showInfo(MESSAGES[reason]);
  }, [location.key, showInfo]);

  return null;
};
